import { accentScope } from "@ui/lib/accent-scope";
import { Link } from "@ui/primitives/Link";
import type { AccentColor } from "@ui/types/accent";
import { cx } from "@ui/utils/cx";
import LogoFull from "./assets/logo-full.svg?react";
import LogoMark from "./assets/logo-mark.svg?react";

const BASE = "inline-flex shrink-0 items-center";

export type LogoVariant = "full" | "mark";
export type LogoColor = AccentColor;

export interface LogoProps {
  variant?: LogoVariant;
  /** Tints the character mark to match a flow's theme (e.g. yellow in the community wizard). Default green. */
  color?: LogoColor;
  /** When provided, renders as a `Link` with this label as `aria-label`. Without
   *  `href` or `label`, the logo is decorative (aria-hidden). */
  href?: string;
  label?: string;
  className?: string;
}

export function Logo({ variant = "full", color = "green", href, label, className }: LogoProps) {
  const Mark = variant === "full" ? LogoFull : LogoMark;
  // Only the character mark is drawn in `currentColor`; the wordmark and outlines carry
  // fixed fills. `green` — the default — reproduces the original #AEBF72 exactly.
  const styles = cx(BASE, "text-icon-dark", className);
  const scope = accentScope(color);

  if (href !== undefined) {
    return (
      <Link {...scope} aria-label={label} className={styles} href={href}>
        <Mark aria-hidden="true" className="h-full w-auto" />
      </Link>
    );
  }

  if (label !== undefined) {
    return (
      <span {...scope} aria-label={label} className={styles} role="img">
        <Mark aria-hidden="true" className="h-full w-auto" />
      </span>
    );
  }

  return (
    <span {...scope} aria-hidden="true" className={styles}>
      <Mark className="h-full w-auto" />
    </span>
  );
}
